/* eslint-disable jsx-a11y/role-supports-aria-props */
import type { ReactElement, Ref } from 'react';

import { useNavigate, useSearch } from '@tanstack/react-router';
import type { Row, Table } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import clsx from 'clsx';

import type { FleetDeviceWithMember } from 'src/api/endpoints/cloudEnterprise.types';
import { TableCell } from 'src/components/Table';
import { useKeyboardNavigation } from 'src/utils/useKeyboardNavigation';

import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { TableDrawer } from './TableDrawer';

interface TableRowProps<T> {
  row: Row<T>;
  table: Table<T>;
}

type TableRowRef = Ref<HTMLTableRowElement>;

function TableRowImpl<T extends FleetDeviceWithMember>({
  row,
  table,
  ref,
}: TableRowProps<T> & { ref?: TableRowRef }) {
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const isActiveRow = search.device === row.original.device.serialNumber;

  return (
    <TableDrawer
      row={{ type: 'user', data: row.original.user }}
      open={isActiveRow}
      onOpenChange={(open) => {
        if (open) {
          void navigate({
            to: '.',
            search: { ...search, device: row.original.device.serialNumber },
            replace: true,
          });
        } else {
          void navigate({
            to: '.',
            search: { ...search, device: undefined, devices: undefined },
            replace: true,
          });
        }
      }}
    >
      <tr
        data-cy={row.id}
        key={row.id}
        className={clsx('group table-row', {
          'bg-paper-4 hover:bg-paper-5': isActiveRow,
        })}
        ref={ref}
        tabIndex={0}
        role="button"
        aria-label={table.options.meta?.getRowLabel?.(row.original)}
        aria-description={table.options.meta?.getRowDescription?.(row.original)}
        onClick={() => {
          tracker.trackEvent(
            new ButtonClicked({
              component_location: ComponentLocations.ENTERPRISE.MEMBERS_PAGE,
              text: '[member table row]',
              action: 'open member details drawer',
            })
          );
        }}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            data-cy={cell.id}
            truncate={cell.column.columnDef.meta?.truncate}
            align={cell.column.columnDef.meta?.align}
            className={clsx(cell.column.columnDef.meta?.colWidth)}
            fullWidth={cell.column.columnDef.meta?.fullWidth}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </tr>
    </TableDrawer>
  );
}

const TableRow = TableRowImpl as <T>(
  props: TableRowProps<T> & { ref?: TableRowRef }
) => ReactElement;

export function DevicesTableBody<T>({ table }: { table: Table<T> }) {
  const rowModel = table.getRowModel();
  const { getRef } = useKeyboardNavigation();

  return (
    <>
      {rowModel.rows.map((row, index) => (
        <TableRow
          table={table}
          row={row}
          key={row.id}
          ref={(el) => getRef(index, el)}
        />
      ))}
    </>
  );
}
